/**
 * useFileUpload.ts
 *
 * Unified hook for all file-upload flows in the app.
 * Replaces useFileUploadManager with a superset feature set.
 *
 * MODES
 *   deferred  (default) – files are staged; call executeUploads() on form submit.
 *   immediate           – each file is uploaded right away via uploadFn.
 *
 * EDIT PAGES
 *   Pass initialUploadedItems to seed existing docs, or call setUploadedItems()
 *   inside a useEffect after async data loads.
 *
 * CREATE PAGES (entity ID unknown at init)
 *   Omit uploadFn from options and pass it to executeUploads(fn) at submit time.
 */
import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { capturePhoto } from '@/services/cameraService';

//  Types 

export interface UploadItemBase {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
}

export interface UseFileUploadOptions<T extends UploadItemBase = UploadItemBase> {
  maxSizeMB: number;
  maxFiles?: number;
  allowedFileTypes: string[];
  uploadMode?: 'deferred' | 'immediate';
  uploadFn?: (file: File) => Promise<T>;
  deleteFn?: (url: string) => Promise<void>;
  initialUploadedItems?: T[];
}

export interface UseFileUploadReturn<T extends UploadItemBase = UploadItemBase> {
  pendingFiles: File[];
  uploadedItems: T[];
  setUploadedItems: (items: T[]) => void;
  isUploading: boolean;
  handleFileUpload: () => void;
  handleCameraCapture: () => Promise<void>;
  handleRemovePendingFile: (index: number) => void;
  handleRemoveUploadedItem: (id: string) => Promise<void>;
  executeUploads: (uploadFnOverride?: (file: File) => Promise<T>) => Promise<T[]>;
  resetPending: () => void;
  resetAll: () => void;
  openImageViewer: (index: number) => void;
  closeImageViewer: () => void;
  imageViewerState: {
    isOpen: boolean;
    images: string[];
    currentIndex: number;
    setCurrentIndex: (index: number) => void;
  };
}

//  Hook 

export function useFileUpload<T extends UploadItemBase = UploadItemBase>(
  options: UseFileUploadOptions<T>
): UseFileUploadReturn<T> {
  const {
    maxSizeMB,
    maxFiles,
    allowedFileTypes,
    uploadMode = 'deferred',
    uploadFn,
    deleteFn,
    initialUploadedItems = [],
  } = options;

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadedItems, setUploadedItemsRaw] = useState<T[]>(initialUploadedItems);
  const [isUploading, setIsUploading] = useState(false);

  // Always-current refs so callbacks don't need closure captures of state lengths
  const pendingCountRef = useRef(0);
  const uploadedCountRef = useRef(0);
  pendingCountRef.current = pendingFiles.length;
  uploadedCountRef.current = uploadedItems.length;
  const [viewingImages, setViewingImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const setUploadedItems = useCallback((items: T[]) => {
    setUploadedItemsRaw(items);
  }, []);

  // Validate + stage or immediately upload a batch of files.
  // Receives current counts explicitly so the callback stays fresh via deps.
  const processSelected = useCallback(
    async (files: File[], currentPendingCount: number, currentUploadedCount: number) => {
      const currentTotal = currentPendingCount + currentUploadedCount;
      if (maxFiles && currentTotal + files.length > maxFiles) {
        toast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const valid = files.filter((f) => {
        if (f.size > maxSizeBytes) {
          toast.error(`${f.name} exceeds the ${maxSizeMB} MB size limit`);
          return false;
        }
        return true;
      });

      if (!valid.length) return;

      if (uploadMode === 'immediate') {
        if (!uploadFn) {
          console.error('[useFileUpload] uploadFn is required for immediate mode');
          return;
        }
        setIsUploading(true);
        const uploaded: T[] = [];
        for (const file of valid) {
          try {
            uploaded.push(await uploadFn(file));
          } catch (err) {
            console.error('[useFileUpload] Upload error:', err);
            toast.error(`Failed to upload ${file.name}`);
          }
        }
        if (uploaded.length > 0) {
          setUploadedItemsRaw((prev) => [...prev, ...uploaded]);
          toast.success(`${uploaded.length} file${uploaded.length !== 1 ? 's' : ''} uploaded`);
        }
        setIsUploading(false);
      } else {
        setPendingFiles((prev) => [...prev, ...valid]);
        toast.success(`${valid.length} file${valid.length !== 1 ? 's' : ''} added`);
      }
    },
    [maxFiles, maxSizeBytes, maxSizeMB, uploadMode, uploadFn]
  );

  const handleFileUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = allowedFileTypes.join(',');
    input.style.display = 'none';
    // Must be in the DOM for Capacitor/Android webview to open the picker
    document.body.appendChild(input);
    const cleanup = () => {
      if (document.body.contains(input)) document.body.removeChild(input);
    };
    input.onchange = (e) => {
      cleanup();
      const target = e.target as HTMLInputElement;
      if (!target.files?.length) return;
      // Use refs — avoids nested setPendingFiles-inside-updater bug in React 18
      processSelected(
        Array.from(target.files),
        pendingCountRef.current,
        uploadedCountRef.current,
      );
    };
    input.addEventListener('cancel', cleanup, { once: true });
    input.click();
  }, [allowedFileTypes, processSelected]);

  const handleCameraCapture = useCallback(async () => {
    let blocked = false;
    setPendingFiles((p) => {
      setUploadedItemsRaw((u) => {
        if (maxFiles && p.length + u.length >= maxFiles) {
          toast.error(`Maximum ${maxFiles} files allowed`);
          blocked = true;
        }
        return u;
      });
      return p;
    });
    if (blocked) return;

    try {
      const photoFile = await capturePhoto(90);
      if (photoFile) {
        setPendingFiles((p) => {
          setUploadedItemsRaw((u) => {
            processSelected([photoFile], p.length, u.length);
            return u;
          });
          return p;
        });
      }
    } catch (err) {
      console.error('[useFileUpload] Camera error:', err);
      toast.error('Failed to capture photo. Please check camera permissions.');
    }
  }, [maxFiles, processSelected]);

  const handleRemovePendingFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveUploadedItem = useCallback(
    async (id: string) => {
      setUploadedItemsRaw((prev) => {
        const item = prev.find((i) => i.id === id);
        if (item?.url && deleteFn) {
          deleteFn(item.url).catch((err) => {
            console.error('[useFileUpload] Delete error:', err);
            toast.error('Failed to delete file');
          });
        }
        return prev.filter((i) => i.id !== id);
      });
    },
    [deleteFn]
  );

  const executeUploads = useCallback(
    async (uploadFnOverride?: (file: File) => Promise<T>): Promise<T[]> => {
      const fn = uploadFnOverride ?? uploadFn;
      if (!fn) {
        console.error('[useFileUpload] uploadFn is required for executeUploads');
        return [];
      }
      if (!pendingFiles.length) return [];

      setIsUploading(true);
      const uploaded: T[] = [];

      for (const file of pendingFiles) {
        try {
          uploaded.push(await fn(file));
        } catch (err) {
          console.error('[useFileUpload] Upload error:', err);
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      setPendingFiles([]);
      if (uploaded.length > 0) {
        setUploadedItemsRaw((prev) => [...prev, ...uploaded]);
      }
      setIsUploading(false);
      return uploaded;
    },
    [pendingFiles, uploadFn]
  );

  const resetPending = useCallback(() => setPendingFiles([]), []);

  const resetAll = useCallback(() => {
    setPendingFiles([]);
    setUploadedItemsRaw([]);
  }, []);

  const openImageViewer = useCallback(
    (index: number) => {
      const imageFiles = pendingFiles.filter((f) => f.type.startsWith('image/'));
      setViewingImages(imageFiles.map((f) => URL.createObjectURL(f)));
      setCurrentImageIndex(index);
      setImageViewerOpen(true);
    },
    [pendingFiles]
  );

  const closeImageViewer = useCallback(() => {
    setImageViewerOpen(false);
    setViewingImages((prev) => {
      prev.forEach((url) => URL.revokeObjectURL(url));
      return [];
    });
    setCurrentImageIndex(0);
  }, []);

  return {
    pendingFiles,
    uploadedItems,
    setUploadedItems,
    isUploading,
    handleFileUpload,
    handleCameraCapture,
    handleRemovePendingFile,
    handleRemoveUploadedItem,
    executeUploads,
    resetPending,
    resetAll,
    openImageViewer,
    closeImageViewer,
    imageViewerState: {
      isOpen: imageViewerOpen,
      images: viewingImages,
      currentIndex: currentImageIndex,
      setCurrentIndex: setCurrentImageIndex,
    },
  };
}
